import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InvitationType } from '../../_graphql/generated/operations';
import InvitationTypeList from '../../_components/InvitationTypeList/InvitationTypeListStandalone';
import InvitationTypeForm, { CreateInvitationTypeFormData } from '../../_components/InvitationTypeForm/InvitationTypeForm';

const InvitationTypes: React.FC = () => {
  const navigate = useNavigate();
  const [invitationTypes, setInvitationTypes] = useState<InvitationType[]>([]);
  const [selectedType, setSelectedType] = useState<InvitationType | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<InvitationType | null>(null);

  // TODO: Replace with actual GraphQL query
  useEffect(() => {
    fetchInvitationTypes();
  }, []);

  const fetchInvitationTypes = async () => {
    try {
      setIsLoading(true);
      // TODO: Implement GraphQL query
      // const { data } = await client.query({ query: GET_INVITATION_TYPES });
      // setInvitationTypes(data.invitationTypes);
      
      // Mock data for now
      setInvitationTypes([]);
    } catch (err) {
      setError('Failed to load invitation types');
      console.error('Error fetching invitation types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateType = () => {
    setEditingType(null);
    setIsFormOpen(true);
  };

  const handleEditType = (invitationType: InvitationType) => {
    setEditingType(invitationType);
    setIsFormOpen(true);
  };

  const handleDeleteType = async (id: string) => {
    if (!confirm('Are you sure you want to delete this invitation type?')) {
      return;
    }

    try {
      // TODO: Implement GraphQL mutation
      // await client.mutate({
      //   mutation: DELETE_INVITATION_TYPE,
      //   variables: { id }
      // });
      
      await fetchInvitationTypes();
    } catch (err) {
      setError('Failed to delete invitation type');
      console.error('Error deleting invitation type:', err);
    }
  };

  const handleFormSubmit = async (data: CreateInvitationTypeFormData) => {
    try {
      if (editingType) {
        // TODO: Implement GraphQL mutation
        // await client.mutate({
        //   mutation: UPDATE_INVITATION_TYPE,
        //   variables: { input: { id: editingType.id, ...data } }
        // });
      } else {
        // TODO: Implement GraphQL mutation
        // await client.mutate({
        //   mutation: CREATE_INVITATION_TYPE,
        //   variables: { input: data }
        // });
      }
      
      setIsFormOpen(false);
      await fetchInvitationTypes();
    } catch (err) {
      setError('Failed to save invitation type');
      console.error('Error saving invitation type:', err);
    }
  };

  const handleTypeSelect = (invitationType: InvitationType) => {
    setSelectedType(invitationType);
  };

  const handleDesignCustomization = (invitationType: InvitationType) => {
    // Navigate to the design page with the invitation type
    navigate(`/designer-v2?typeId=${invitationType.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading invitation types...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Invitation Types</h1>
        <p className="text-gray-600 mt-2">
          Manage different invitation designs for your events
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">All Invitation Types</h2>
              <button 
                onClick={handleCreateType}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Type
              </button>
            </div>
            <div className="p-6">
              {invitationTypes.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">
                    No invitation types created yet
                  </p>
                  <button 
                    onClick={handleCreateType}
                    className="border border-gray-300 hover:border-gray-400 text-gray-700 px-4 py-2 rounded-md transition-colors"
                  >
                    Create Your First Type
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {invitationTypes.map((type) => (
                    <div
                      key={type.id}
                      className={`p-4 border rounded-lg transition-colors ${
                        selectedType?.id === type.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleTypeSelect(type)}
                        >
                          <h3 className="font-semibold text-gray-900">
                            {type.name}
                          </h3>
                          {type.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {type.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Event: {type.event?.eventName || 'Unknown'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleDesignCustomization(type)}
                            className="px-3 py-1 text-sm border border-gray-300 hover:border-gray-400 rounded-md transition-colors"
                          >
                            Customize Design
                          </button>
                          <button
                            onClick={() => handleEditType(type)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteType(type.id)}
                            className="p-1 hover:bg-red-50 rounded transition-colors"
                          >
                            <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          {selectedType && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Invitation Type Details
              </h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="text-sm text-gray-900">{selectedType.name}</dd>
                </div>
                {selectedType.description && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="text-sm text-gray-900">{selectedType.description}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Event</dt>
                  <dd className="text-sm text-gray-900">
                    {selectedType.event?.eventName || 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="text-sm text-gray-900">
                    {selectedType.createdAt ? new Date(selectedType.createdAt).toLocaleDateString() : 'Unknown'}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => handleDesignCustomization(selectedType)}
                  className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors"
                >
                  Customize Design
                </button>
                <button
                  onClick={() => handleEditType(selectedType)}
                  className="w-full border border-gray-300 hover:border-gray-400 text-gray-700 px-4 py-2 rounded-md transition-colors"
                >
                  Edit Details
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full m-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingType ? 'Edit Invitation Type' : 'Create Invitation Type'}
              </h3>
            </div>
            <div className="p-6">
              <InvitationTypeForm
                initialData={editingType ? {
                  name: editingType.name,
                  description: editingType.description || '',
                  eventId: editingType.event?.id || ''
                } : undefined}
                onSubmit={handleFormSubmit}
                onCancel={() => setIsFormOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationTypes;